import * as amplitude from "@amplitude/analytics-browser";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spacer,
  Tag,
  Text,
} from "@chakra-ui/react";
import { Form, FormikProps } from "formik";
import { RiLightbulbLine, RiQuestionLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { CheckboxGroup } from "src/components/Inputs/CheckboxGroup";
import { FormikSwitchInput } from "src/components/Inputs/FormikSwitchInput";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { ColumnGroup } from "./columnUtils";
import { BulkExportFormType, ExportFormPage } from "./types";

interface SelectColumnsPageProps {
  columnGroups: ColumnGroup[];
  setCurrentPage: React.Dispatch<React.SetStateAction<ExportFormPage>>;
}

export const SelectColumnsPage = (
  props: FormikProps<BulkExportFormType> & SelectColumnsPageProps
) => {
  const navigate = useNavigate();

  return (
    <Flex as={Form} direction="column" flexGrow="1" gap={3}>
      <HStack>
        <Heading size="lg">Select columns</Heading>
        <Popover placement="right-end" trigger="hover">
          <PopoverTrigger>
            <IconButton
              size="xl"
              icon={<RiQuestionLine />}
              variant="ghost"
              aria-label="Select columns"
              color="black"
            />
          </PopoverTrigger>
          <PopoverContent>
            <Box margin={6}>
              <Tag size="sm" variant="solid" colorScheme="purple" mb={2}>
                <Icon boxSize={4} as={RiLightbulbLine} paddingRight={1} />
                <Text>Tip</Text>
              </Tag>
              <Heading as="h1" size="md">
                Customize your exported columns
              </Heading>
              <Spacer minH={2} />
              <Text fontSize="sm">
                Pick which columns to keep by selecting only the ones you're
                interested in.
              </Text>
            </Box>
          </PopoverContent>
        </Popover>
      </HStack>
      <Text>The export will include only the following columns:</Text>

      <Flex direction="column" gap={0}>
        <FormikSwitchInput<boolean>
          name="options.questionKeysAsHeaders"
          label="Export column headers as keys"
          formLabelProps={{
            flexDirection: "row-reverse",
            fontSize: "md",
          }}
          checked={[true, ""]}
          onChange={(event) => {
            amplitude.track("Toggled column headers as keys", {
              column_headers_as_keys: event.target.checked
                ? "is_active"
                : "is_inactive",
            });
            console.log("toggled column headers as keys", event.target.checked);
          }}
        />
        <Text ml={12} fontSize="sm" color="gray.600">
          When enabled, column headers will use each question’s key instead of
          its title. If a question doesn’t have a key, its title will be used
          instead.
        </Text>
      </Flex>

      <Flex direction="column" gap={0}>
        <FormikSwitchInput<boolean>
          name="options.exportSelectAnswerValues"
          label="Export row cells as values"
          formLabelProps={{
            flexDirection: "row-reverse",
            fontSize: "md",
          }}
          checked={[true, ""]}
          onChange={(event) => {
            amplitude.track("Toggled row cells as values", {
              row_cells_as_values: event.target.checked
                ? "is_active"
                : "is_inactive",
            });
            console.log("toggled row cells as values", event.target.checked);
          }}
        />
        <Text ml={12} fontSize="sm" color="gray.600">
          When enabled, select-type question answers will be expressed as
          values, depending on the field type.
        </Text>
      </Flex>

      {props.columnGroups.map((group, idx) => (
        <CheckboxGroup
          key={idx}
          {...group}
          values={props.values.selectedColumns}
          onChange={(key, selected) => {
            props.setFieldValue(`selectedColumns[${key}]`, selected);
          }}
        />
      ))}

      <Spacer minH={8} />

      <AdminFormButtons>
        <Button
          variant="outline"
          colorScheme="gray"
          onClick={() => {
            amplitude.track("Clicked back");
            console.log("clicked back");
            navigate(-1);
          }}
        >
          Back
        </Button>
        <Spacer />
        <Button
          variant="solid"
          onClick={() => {
            amplitude.track("Clicked next");
            console.log("clicked next");
            props.setCurrentPage(ExportFormPage.ConfigureExport);
          }}
        >
          Next
        </Button>
      </AdminFormButtons>
    </Flex>
  );
};
